import {Typography, useTheme, css} from '@mui/material';
import {TranslateUtils, enumContainer, DlMuiChip, DlMuiPopper, GetStylesFunction, useMuiAnchor, rawRenderingLib} from '@wallaby-ui/core';
import {isNumber} from 'lodash';
import * as React from 'react';
import {useIntl} from 'react-intl';
import {useStore} from 'react-redux';
import {MerieSchema} from 'src/js/common/types';
import {TemplatedCellRendererParams} from 'src/js/containers/grid';
import {RISK_FACTOR_LABEL_COLORS, getRiskFactorMedicationLabel, sortRiskFactorsData} from 'src/js/containers/hospitalizations';

type Props = TemplatedCellRendererParams<MerieSchema.components['schemas']['EvaluatedEncounterDto']>;

export const RiskFactorsFormatter: React.FC<Props> = (props) => {
  const {value, data} = props;
  const hasTooltip = value != null && value !== 0;
  const {mouseEventEnter, mouseEventLeave, open, anchorEl} = useMuiAnchor(hasTooltip);

  const theme = useTheme();
  const styles = getStyles({theme});
  const intl = useIntl();
  const store = useStore();
  const state = store && store.getState();
  const {riskFactors} = data;

  const sortedRiskFactorsData = React.useMemo(
    () =>
      sortRiskFactorsData(
        (riskFactors ?? []).map((riskFactor) => {
          // REFACTOR: useFormatters()
          const riskFactorTypeEnumValue = enumContainer.getEnumValue(state, 'RiskFactorTypeEnum', riskFactor.type);
          const translatedRiskFactorTypeEnumValue =
            riskFactorTypeEnumValue && intl && TranslateUtils.translate(riskFactorTypeEnumValue, intl);

          const riskFactorNtidxmEnumValue = enumContainer.getEnumValue(
            state,
            'NarrowTherapeuticIndexMedicationEnum',
            riskFactor?.narrowTherapeuticIndexMedication || ''
          );
          const translatedRiskFactorNtidxmEnumValue =
            riskFactorNtidxmEnumValue && intl && TranslateUtils.translate(riskFactorNtidxmEnumValue, intl);

          const {plainTranslatedValue: translatedRiskFactorLabel} = rawRenderingLib.baseTranslatedEnumValueParams(
            {
              enumName: 'RiskFactorLabelEnum',
              enumValueName: riskFactor.labels[0],
            },
            {intl, store}
          );
          return {
            ...riskFactor,
            translatedType: translatedRiskFactorTypeEnumValue,
            narrowTherapeuticIndexMedication: translatedRiskFactorNtidxmEnumValue,
            translatedRiskFactorLabel,
          };
        }),
        new Set()
      ),
    [riskFactors, intl, state, store]
  );

  return (
    <Typography variant={'value'} data-name={'risk-factors-formatter--wrapper'}>
      <span css={styles.span} onMouseEnter={mouseEventEnter} onMouseLeave={mouseEventLeave} data-name={'risk-factors-formatter--value'}>
        {isNumber(value) ? value : 0}
      </span>
      <DlMuiPopper
        data-name={'risk-factors-formatter--popper'}
        open={open}
        anchorEl={anchorEl}
        hasTooltip={hasTooltip}
        inputCss={styles.content}
      >
        {sortedRiskFactorsData.map((riskFactor) => {
          return (
            <div key={riskFactor.id} css={styles.riskFactorWrapper} data-name='risk-factors-formatter--popper-content'>
              <DlMuiChip
                label={riskFactor.translatedRiskFactorLabel}
                inputCss={styles.riskFactorLabel}
                style={{
                  backgroundColor: RISK_FACTOR_LABEL_COLORS[riskFactor.labels[0]]?.color,
                }}
              />
              <Typography variant='valueSmall' css={styles.riskFactorLabel}>
                {(riskFactor.type === 'OTHER' ? riskFactor.typeOtherUserGivenName : riskFactor.translatedType) +
                  (getRiskFactorMedicationLabel(riskFactor) ? ` - ${getRiskFactorMedicationLabel(riskFactor)}` : '')}
              </Typography>
            </div>
          );
        })}
      </DlMuiPopper>
    </Typography>
  );
};

RiskFactorsFormatter.displayName = 'RiskFactorsFormatter';

const getStyles: GetStylesFunction = (options) => ({
  span: css`
    padding: ${options?.theme?.spacing(1, 0)};
  `,
  content: css`
    display: flex;
    flex-direction: column;
    row-gap: ${options?.theme?.spacing(2)};
    max-width: 400px;
  `,
  riskFactorWrapper: css`
    display: flex;
    gap: 4px;
    justify-content: start;
    align-items: center;
    word-break: break-word;
  `,
  riskFactorLabel: css`
    color: white;
  `,
});
